<script lang="ts">
  import PenPopover from './PenPopover.svelte';
  import SyncDot from './SyncDot.svelte';
  import { app } from './appState.svelte';
  import type { InkEngine, Tool } from '../ink/engine';

  let {
    engine, writing, canUndo, canRedo, pageLabel, isInfinite, candeletepage, historyEnabled, historyMode, exporting,
    onback, onprev, onnext, onaddpage, ondeletepage, onpages, onhistory, ontoolchange, onexport,
  }: {
    engine: InkEngine | null; writing: boolean; canUndo: boolean; canRedo: boolean;
    pageLabel: string; isInfinite: boolean; candeletepage: boolean;
    historyEnabled: boolean; historyMode: boolean; exporting: boolean;
    onback: () => void; onprev: () => void; onnext: () => void; onaddpage: () => void; ondeletepage: () => void;
    onpages: () => void; onhistory: () => void; ontoolchange: (t: Tool) => void; onexport: () => void;
  } = $props();

  let tool = $state<Tool>('pen');
  let showPen = $state(false);

  // Single writer for the engine's tool: covers both user tool switches (tool
  // changes) and page navigation (a new engine instance arrives with its
  // default 'pen' tool while this Toolbar instance survives).
  $effect(() => { if (engine) engine.setTool(tool); });

  function pick(t: Tool) {
    // Re-tapping the already-active tool (pen/highlighter/either eraser) opens
    // its size/color popover instead of being a no-op. Lasso/text have none.
    if (t === tool) { if (t !== 'lasso' && t !== 'text') showPen = !showPen; return; }
    tool = t;
    showPen = false;
    ontoolchange(t); // Editor saves+closes the text overlay when leaving 'text'
  }

  function paste() {
    if (!engine || !app.clipboard) return;
    // The engine switches itself to lasso (pasted set becomes the selection);
    // mirror that in the local tool state, which is the single writer.
    engine.pasteClipboard(app.clipboard);
    tool = 'lasso';
    showPen = false;
    ontoolchange('lasso');
  }

  // ---- insert image (M4 spec §3): picker → upload (or local-only) → engine ----
  let imageInput: HTMLInputElement | undefined;
  let inserting = $state(false);
  const IMAGE_MAX_BYTES = 25 * 1024 * 1024;

  async function insertImage(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // re-selecting the same file must fire change again
    if (!file || !engine || inserting) return;
    if (file.size > IMAGE_MAX_BYTES) { app.showToast('Image is too large (25MB max).'); return; }
    inserting = true;
    try {
      const bytes = await file.arrayBuffer();
      // Decode once for the natural size (also validates the image bytes
      // BEFORE anything is uploaded or persisted).
      const bmp = await createImageBitmap(new Blob([bytes], { type: file.type }));
      const { width, height } = bmp;
      bmp.close();
      const localOnly = app.syncStatus === 'disabled';
      const fileId = await app.uploadFile(bytes, file.name, file.type);
      if (localOnly) app.showToast('Saved locally — enable sync before inserting to share across devices.');
      await engine.insertImage(fileId, width, height);
      // insertImage switched the engine to lasso (the new image is selected);
      // mirror it in the local tool state, the single writer — same pattern
      // as pasteClipboard.
      tool = 'lasso';
      showPen = false;
      ontoolchange('lasso');
    } catch (e) {
      app.showToast(`Couldn't insert image: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      inserting = false;
    }
  }
</script>

<div class="toolbar" class:writing>
  <div class="group">
    <button onclick={onback} title="Library">☰</button>
    <button onclick={onpages} disabled={historyMode} title="Pages">▤</button>
    <button onclick={onprev} disabled={historyMode} title="Previous page">‹</button>
    <span class="pageLabel">{pageLabel}</span>
    <button onclick={onnext} disabled={historyMode} title="Next page">›</button>
    <button onclick={onaddpage} disabled={historyMode} title="Add page">＋</button>
    <button onclick={ondeletepage} disabled={!candeletepage || historyMode} title="Delete page">🗑</button>
  </div>
  <div class="group tools">
    <button class:active={tool === 'pen'} disabled={historyMode} onclick={() => pick('pen')} title="Pen">✒️</button>
    <button class:active={tool === 'highlighter'} disabled={historyMode} onclick={() => pick('highlighter')} title="Highlighter">🖍</button>
    <button class:active={tool === 'eraser-area'} disabled={historyMode} onclick={() => pick('eraser-area')} title="Eraser">◧</button>
    <button class:active={tool === 'eraser-stroke'} disabled={historyMode} onclick={() => pick('eraser-stroke')} title="Stroke eraser">⌫</button>
    <button class:active={tool === 'lasso'} disabled={historyMode} onclick={() => pick('lasso')} title="Lasso">⬚</button>
    <button class="t" class:active={tool === 'text'} disabled={historyMode} onclick={() => pick('text')} title="Text">T</button>
    {#if app.clipboard}
      <button onclick={paste} disabled={!engine || historyMode} title="Paste">📋</button>
    {/if}
    <button onclick={() => imageInput?.click()} disabled={!engine || historyMode || inserting} title="Insert image">🖼</button>
    <button disabled={!canUndo || historyMode} onclick={() => engine?.undo()} title="Undo">↺</button>
    <button disabled={!canRedo || historyMode} onclick={() => engine?.redo()} title="Redo">↻</button>
    <!-- Also disabled mid-stroke: an in-flight stroke must not cross into
         preview (the engine would drop it — don't offer the footgun). -->
    <button
      class:active={historyMode}
      disabled={!engine || !historyEnabled || historyMode || writing}
      onclick={onhistory}
      title="History"
    >⏱</button>
    {#if isInfinite}
      <button onclick={() => engine?.jumpToContent()} disabled={historyMode} title="Jump to content">⌖</button>
    {/if}
  </div>
  <div class="group">
    <button onclick={onexport} disabled={!engine || historyMode || writing || exporting} title="Export PDF">⤓</button>
    <SyncDot status={app.syncStatus} />
    <button onclick={() => app.setThemeOverride(app.theme === 'dark' ? 'light' : 'dark')} title="Theme">
      {app.theme === 'dark' ? '☀️' : '🌙'}
    </button>
  </div>
</div>

<!-- Hidden picker for 🖼 (spec §3: png/jpeg/webp only, ≤25MB). -->
<input type="file" accept="image/png,image/jpeg,image/webp" hidden bind:this={imageInput} onchange={insertImage} />

{#if showPen && engine}
  <PenPopover {engine} onclose={() => (showPen = false)} />
{/if}

<style>
  .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 8px 12px; padding-top: max(8px, env(safe-area-inset-top)); background: var(--surface); border-bottom: 1px solid var(--hairline); transition: opacity 180ms ease-out; z-index: 10; }
  .toolbar.writing { opacity: 0.4; }
  .group { display: flex; align-items: center; gap: 4px; }
  button { width: 44px; height: 44px; border: none; border-radius: var(--radius-panel); background: transparent; color: var(--ink-primary); font-size: 17px; cursor: pointer; }
  button.active { background: var(--accent); color: #fff; }
  button:disabled { opacity: 0.3; }
  button.t { font-weight: 700; font-family: 'Nunito Sans', ui-rounded, 'Segoe UI', system-ui, sans-serif; }
  .pageLabel { font-size: 13px; opacity: 0.7; min-width: 48px; text-align: center; }
</style>
